/* Hosted workspace-changes store + pure join/badge helpers (spec docs/superpowers/specs/
 * 2026-08-24-workspace-changes-design.md). Owns the last-fetched payload from
 * GET /api/connections/{cid}/workspace-changes and answers "is this row's file
 * agent-changed" for the Code/Templates views. The DOM layers subscribe via WSC_EVENT and
 * re-render; nothing here touches the DOM, so every join is node-testable.
 * window.__test_workspace.set() is the deterministic e2e hook, mirroring
 * __test_code.setRows(): inject a payload, then re-list — badges render through the same
 * joins a real fetch uses. */
import { api } from "./api.js";
import { getActiveConnection, getAuthMode } from "./auth.js";

export const WSC_EVENT = "workspace-changes-updated";

// state.js's escAttr() round-trips the value through document.createElement, which does
// not exist under plain `node --test` -- and this module's whole reason for existing (see
// header comment) is being DOM-free and node-testable, so pulling in that DOM-dependent
// helper would silently break the one property this module is built to guarantee. Same
// escaping contract (&<>"'), implemented with regex instead of a live DOM round-trip.
function _escAttr(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

let _payload = null; // last GET body ({files, snapshotAt, pullId?, pulledAt?, degraded?}) or null

export function getWorkspaceChanges() {
  return _payload;
}

// Structural equality by content, not reference -- refreshWorkspaceChanges() polls on a
// timer/every agent_final, and the overwhelmingly common case is "nothing changed since
// the last fetch" (a brand-new object from the SAME unchanged server state). Payloads are
// small (a handful of file entries), so a JSON serialize-compare is cheap and exact enough;
// it only needs to catch the "truly nothing changed" case, not diff large structures.
function _samePayload(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export function setWorkspaceChanges(payload) {
  const next = payload || null;
  // Dedupe: an identical (by content) payload is a no-op -- no store update, no WSC_EVENT.
  // Without this, every agent_final's refreshWorkspaceChanges() call fires the event even
  // when nothing actually changed, and every subscriber (code-view.js's _onWscUpdate) pays
  // for a full re-list it didn't need, including the scroll-position save/restore dance --
  // skipping the no-op case entirely is simpler and cheaper than making every subscriber
  // independently detect "nothing to do".
  if (_samePayload(_payload, next)) return;
  _payload = next;
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(WSC_EVENT));
}

// Fold a push/discard response's `workspace` payload into the stored one, applying it for
// ONE path only. Deliberately not a wholesale replacement: the server recomputes that
// payload right after invalidating the connection's artifact content cache, so on a
// degraded connection any sibling that was only visible via cloud-compare is momentarily
// missing from it (and the remaining files can flip to a cloud-compared verdict) even
// though nothing about those files actually changed. Replacing the store with it would
// blank their badges until the next poll refills the cache. So: the acted-on path takes
// its new state (or leaves the list, when the server no longer reports it as changed),
// every OTHER entry keeps exactly the state it already had, and an entry the server newly
// reports is added. pullId/pulledAt describe the last PULL, which neither push nor discard
// can move -- a response that omits them lost a ledger read, it did not lose the pull -- so
// they fall back to what is already stored. snapshotAt and `degraded` come from the
// response verbatim (a discard genuinely repacks the snapshot; a cleared `degraded` must
// clear here too).
export function mergeWorkspaceForPath(prev, next, path) {
  if (!next) return prev || null;
  if (!prev || !Array.isArray(prev.files)) return next;
  const nextFiles = Array.isArray(next.files) ? next.files : [];
  const byPath = new Map(nextFiles.map((f) => [f.path, f]));
  const seen = new Set();
  const files = [];
  for (const f of prev.files) {
    seen.add(f.path);
    if (f.path !== path) {
      files.push(f);
      continue;
    }
    const updated = byPath.get(path);
    if (updated) files.push(updated); // still changed (e.g. the tenant stored a sanitised body)
  }
  for (const f of nextFiles) {
    if (!seen.has(f.path)) files.push(f);
  }
  const merged = { ...next, files };
  if (merged.pullId == null && prev.pullId != null) merged.pullId = prev.pullId;
  if (merged.pulledAt == null && prev.pulledAt != null) merged.pulledAt = prev.pulledAt;
  return merged;
}

export async function refreshWorkspaceChanges() {
  if (getAuthMode() !== "hosted" || !getActiveConnection()) return null;
  try {
    setWorkspaceChanges(await api.workspaceChanges());
  } catch {
    /* additive surface: a failed load renders exactly as before (no badges, no error) */
  }
  return _payload;
}

// sync.py's _safe_action_name, ported byte-for-byte (src/cpq_agent/sync.py:1785-1790):
// re.sub(r"[^\w\-]", "_", name.strip()) then re.sub(r"_+", "_", safe).strip("_") -- quote-table
// action display names map to on-disk actions/<safe>.py members this way. Python's `\w` is
// Unicode-aware (re.UNICODE is the Python 3 default), so a diacritic like "ț"/"ă"/"â" (a real
// Romanian-tenant action name, e.g. societatea-dev) counts as a word character there -- JS's
// `\w` is ASCII-only, so `\p{L}\p{N}` (Unicode letter/number categories) stands in for it here.
// The trailing edge-strip (`.strip("_")`/the final `replace(/^_+|_+$/g, "")` below) IS kept,
// matching Python exactly: "Validate!" -> "Validate" (not "Validate_"), "Notify (Manager)" ->
// "Notify_Manager" (not "Notify_Manager_").
export function safeActionName(name) {
  const safe = String(name || "")
    .trim()
    .replace(/[^\p{L}\p{N}_-]/gu, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return safe || "unnamed";
}

function _files() {
  return _payload && Array.isArray(_payload.files) ? _payload.files : [];
}

export function changeForPath(path) {
  return _files().find((f) => f.path === path) || null;
}

// Custom template rows carry only a NAME; workspace members are hierarchical
// ui-templates/<Page>/<Slot...>/<Name>.html — match by basename.
export function changeForTemplateName(name) {
  const suffix = `/${name}.html`;
  return (
    _files().find((f) => f.path.startsWith("ui-templates/") && f.path.endsWith(suffix)) || null
  );
}

// Accepts the Code view's _fileNameForRow grammar (cloud paths) AND raw member paths:
// flat scripts/calcs match directly; quote-tables/<T>/<Action>.py maps to the on-disk
// quote-tables/<T>/actions/<safe>.py member; ui-templates/<Name>.html matches by basename.
export function changeForFilePath(path) {
  const direct = changeForPath(path);
  if (direct) return direct;
  const qt = /^quote-tables\/([^/]+)\/([^/]+)\.py$/.exec(path || "");
  if (qt) return changeForPath(`quote-tables/${qt[1]}/actions/${safeActionName(qt[2])}.py`);
  const tpl = /^ui-templates\/([^/]+)\.html$/.exec(path || "");
  if (tpl) return changeForTemplateName(tpl[1]);
  return null;
}

// VS Code SCM-style: amber M (modified) / green A (new). comparedTo rides the tooltip so a
// cloud-compared (degraded) verdict is visibly labeled, never passed off as baseline-compared.
export function workspaceBadgeHtml(change) {
  if (!change) return "";
  const isNew = change.state === "new";
  const vs = change.comparedTo === "cloud" ? "current cloud version" : "pulled baseline";
  const title = `Agent-${isNew ? "created" : "modified"} · differs from the ${vs}`;
  return `<span class="wsc-badge ${isNew ? "a" : "m"}" title="${_escAttr(title)}">${isNew ? "A" : "M"}</span>`;
}

// {global, calcs, table} changed-file counts for the Code rail header. ui-templates changes
// belong to the Templates view, not a Code segment, so they are deliberately not counted here.
export function changedCountsBySegment(files) {
  const counts = { global: 0, calcs: 0, table: 0 };
  for (const f of files || []) {
    if (f.path.startsWith("global-scripts/")) counts.global++;
    else if (f.path.startsWith("custom-calculations/")) counts.calcs++;
    else if (f.path.startsWith("quote-tables/")) counts.table++;
  }
  return counts;
}

if (typeof window !== "undefined") {
  window.__test_workspace = { set: setWorkspaceChanges };
}
